import { generationCapabilityBlocker, hasSupportedProgrammingConfiguration } from 'app/hyperion/exercise-generation/hyperion-generation-capabilities';
import { ExerciseGenerationCapabilities } from 'app/openapi/model/exercise-generation-capabilities';
import { ProgrammingExercise, ProgrammingLanguage } from 'app/programming/shared/entities/programming-exercise.model';
import { supportsHyperionExerciseGeneration } from 'app/hyperion/exercise-generation/hyperion-generation-support';
import { TumAetUiTagSeverity } from '@tumaet/ui-angular';
import { DifficultyLevel, Exercise, ExerciseType } from 'app/exercise/shared/entities/exercise/exercise.model';
import { QuizExercise } from 'app/quiz/shared/entities/quiz-exercise.model';
import { QuizQuestionType } from 'app/quiz/shared/entities/quiz-question.model';
import { VariantGenerationRequest } from 'app/openapi/model/variant-generation-request';

export type PlacementChoice = 'existing-group' | 'new-group' | 'standalone';

/** Quiz keeps its existing support rule; programming uses the isolated authoring configuration. */
export function supportsAiVariantGeneration(exercise: Exercise | undefined): boolean {
    if (!exercise) {
        return false;
    }
    switch (exercise.type) {
        case ExerciseType.PROGRAMMING:
            return hasSupportedProgrammingConfiguration(exercise);
        case ExerciseType.QUIZ:
            return !quizHasDragAndDropQuestions(exercise);
        default:
            return false;
    }
}

/** Variants create a new exercise, so release dates and existing participations do not block them. */
export function variantGenerationBlocker(exercise: Exercise | undefined, capabilities: ExerciseGenerationCapabilities | undefined, loading = false): string | undefined {
    const prefix = 'artemisApp.hyperion.generation.blocker.';
    if (!exercise?.id) return prefix + 'saveBeforeVariant';
    if (!exercise.isAtLeastEditor) return prefix + 'requiresEditor';
    if (exercise.type !== ExerciseType.PROGRAMMING) return supportsAiVariantGeneration(exercise) ? undefined : prefix + 'variantUnavailable';
    const programming = exercise as ProgrammingExercise;
    if (programming.exerciseGroup) return prefix + 'examExercise';
    if (programming.programmingLanguage !== ProgrammingLanguage.JAVA) return prefix + 'unsupportedLanguage';
    if (!supportsHyperionExerciseGeneration(programming.programmingLanguage, programming.projectType)) return prefix + 'unsupportedProjectType';
    if (programming.staticCodeAnalysisEnabled) return prefix + 'staticCodeAnalysis';
    if (programming.buildConfig?.sequentialTestRuns) return prefix + 'sequentialTestRuns';
    if (programming.auxiliaryRepositories?.length) return prefix + 'auxiliaryRepositories';
    if (loading) return prefix + 'capabilitiesLoading';
    if (!capabilities) return prefix + 'capabilitiesUnavailable';
    if (capabilities.canCreateVariant) return undefined;
    switch (capabilities.restriction) {
        case 'generationHarnessUnsupported':
        case 'generationHarnessUnavailable':
            return generationCapabilityBlocker(capabilities) ?? prefix + 'variantUnavailable';
        default:
            return prefix + 'variantUnavailable';
    }
}

/**
 * List endpoints ship `hasDragAndDropQuestions` without the question graph; views that load full quizzes (the exam
 * exercise groups) only have `quizQuestions`. Prefer the explicit flag and fall back to the loaded questions.
 */
function quizHasDragAndDropQuestions(quiz: QuizExercise): boolean {
    if (quiz.hasDragAndDropQuestions !== undefined) {
        return quiz.hasDragAndDropQuestions;
    }
    return (quiz.quizQuestions ?? []).some((question) => question.type === QuizQuestionType.DRAG_AND_DROP);
}

/** Longest custom instruction rendered in a chip before it is elided — the tray card is narrow. */
const CUSTOM_CHIP_MAX_LENGTH = 80;

/** Translation key of a difficulty level — reuses the shared exercise translations. */
export function difficultyTranslationKey(d: DifficultyLevel): string {
    return `artemisApp.exercise.${d.toLowerCase()}`;
}

/** Translation key of a narrative style's label, e.g. CREATIVE → …wizard.narrative.CREATIVE. */
export function narrativeStyleTranslationKey(style: string): string {
    return `artemisApp.exerciseVariantGeneration.wizard.narrative.${style}`;
}

/**
 * Resolves a translation key with optional interpolation parameters. Passed in by the calling component rather
 * than resolved here, so these helpers stay pure functions (same pattern as `buildCourseExerciseCards`).
 */
export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/** "What is being adapted" chips derived from a generation request — shared by the wizard modal and the tray cards. */
export function adaptationChips(request: VariantGenerationRequest | undefined, translate: TranslateFn): string[] {
    if (!request) {
        return [];
    }
    const chip = (name: string, value: string) => translate(`artemisApp.exerciseVariantGeneration.chip.${name}`, { value });
    const items: string[] = [];
    if (request.targetDifficulty) {
        items.push(chip('difficulty', translate(difficultyTranslationKey(request.targetDifficulty as DifficultyLevel))));
    }
    if (request.domainText) {
        items.push(chip('domain', request.domainText));
    }
    if (request.narrativeStyle) {
        items.push(chip('story', translate(narrativeStyleTranslationKey(request.narrativeStyle))));
    }
    if (request.additionalInstructions) {
        const instructions = request.additionalInstructions;
        items.push(chip('custom', instructions.length > CUSTOM_CHIP_MAX_LENGTH ? `${instructions.slice(0, CUSTOM_CHIP_MAX_LENGTH)}…` : instructions));
    }
    return items;
}

/** Severity of the `tumaet-ui-tag` showing an exercise's difficulty. */
export function difficultySeverity(d: DifficultyLevel | undefined): TumAetUiTagSeverity {
    switch (d) {
        case DifficultyLevel.EASY:
            return 'success';
        case DifficultyLevel.MEDIUM:
            return 'warn';
        case DifficultyLevel.HARD:
            return 'danger';
        default:
            return 'secondary';
    }
}

export function durationDays(exercise: Exercise): number {
    if (exercise.releaseDate && exercise.dueDate) {
        return Math.max(1, exercise.dueDate.diff(exercise.releaseDate, 'day'));
    }
    return 7;
}
